// Lib
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';
import type { Editor } from '@tiptap/react';

// Utils
import { getElementId } from '../../../../common/elements/utils/elementPropertyUtils';
import requestSecondAnimationFrame from '../../../../common/utils/lib/requestSecondAnimationFrame';
import { getMainEditorId, getMainEditorKey } from '../../utils/elementEditorUtils';

// Hooks
import { useTiptapEditorByIdCallback } from '../../../components/tiptapEditor/store/tiptapEditorStoreHooks';

// Actions
import { setSelectedElements, startEditingElement } from '../../selection/selectionActions';

// Constants
import { TiptapNodeType } from '../../../../common/tiptap/tiptapTypes';

// Types
import type { ImMNElement } from '../../../../common/elements/elementModelTypes';

const MAX_FOCUS_ATTEMPTS = 3;

export type AddTaskListTitleArgs = {
    element: ImMNElement;
    isFocusedForegroundElement?: boolean;
    /** Element changes to persist in the same update as the added title. */
    additionalChanges?: Record<string, unknown>;
};

// Identifies this hook as the owner of the paused synchroniser (see `pauseUpdates`)
const ADD_TITLE_SYNC_KEY = 'addTaskListTitle';

const getTitleFocusPosition = (editor: Editor): number => {
    const titleNode = editor.state.doc.firstChild;

    return titleNode?.type.name === TiptapNodeType.taskListTitle ? titleNode.nodeSize - 1 : 0;
};

/**
 * Returns a callback that adds the optional title to a single task list editor, then starts editing
 * the element and places the caret in the new title so it can be typed into straight away.
 *
 * The editor instance is taken from the Tiptap editor registry so the title node is added to the
 * live document rather than to the (debounced) persisted element content.
 */
const useAddTaskListTitleAndStartEditing = (): ((args: AddTaskListTitleArgs) => void) => {
    const dispatch = useDispatch();
    const getTiptapEditorById = useTiptapEditorByIdCallback();

    // The element only becomes editable once the editing state has been dispatched and rendered,
    // so keep retrying until the editor has taken focus.
    const focusTitle = useCallback(
        (editorId: string): void => {
            const attemptFocusTitle = (attempts: number): void => {
                requestAnimationFrame(() => {
                    const editor = getTiptapEditorById(editorId);

                    if (!editor?.isFocused) {
                        if (attempts > 1) attemptFocusTitle(attempts - 1);
                        return;
                    }

                    editor.commands.focus(getTitleFocusPosition(editor));
                });
            };

            attemptFocusTitle(MAX_FOCUS_ATTEMPTS);
        },
        [getTiptapEditorById],
    );

    return useCallback(
        (args: AddTaskListTitleArgs): void => {
            const { element, additionalChanges } = args;
            const editorId = getMainEditorId(args);
            const editor = getTiptapEditorById(editorId);

            // The title is a node in the live document, so there's nothing to add without an editor
            if (!editor) return;

            // Editing an element that isn't selected leaves it in a state it can't be got into by
            // hand, so select it first. Already being the only selected element makes this a no-op.
            dispatch(
                setSelectedElements({
                    ids: [getElementId(element)],
                    rangeAnchors: undefined,
                    transactionId: undefined,
                }),
            );

            // Editing starts before the content is saved: a save while the element isn't being edited
            // syncs the editor back from the store, which would put the caret at the end of the
            // document rather than in the new title (see useTiptapEditorPersistedContentSync).
            dispatch(
                startEditingElement({
                    id: getElementId(element),
                    editorId,
                    editorKey: getMainEditorKey(args),
                    editorFocusRect: undefined,
                    transactionId: undefined,
                }),
            );

            // Adding the title changes the line count, which would otherwise save on its own -
            // pausing lets the flush below carry `additionalChanges` in that same update.
            editor.storage.externalStoreSynchroniser.pauseUpdates(ADD_TITLE_SYNC_KEY);

            try {
                editor.commands.addTaskListTitle();
            } finally {
                editor.storage.externalStoreSynchroniser.resumeUpdates(ADD_TITLE_SYNC_KEY);
            }

            editor.commands.flushPendingUpdate({ additionalChanges });

            requestSecondAnimationFrame(() => focusTitle(editorId));
        },
        [dispatch, focusTitle, getTiptapEditorById],
    );
};

export default useAddTaskListTitleAndStartEditing;
